<template>
  <b-card
    no-body
    class="complex-table-view p-3"
    :style="getTableViewStyle"
  >
    <div class="d-flex mb-3 justify-content-between main-header-wrapper">
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="perPage"
          class="per-page-select mr-2"
          :options="perPageOptions"
          @change="onPerPageChange"
        />
      </div>
      <filter-header
        :filters="filters"
        @filter="onFilter"
      />
      <b-dropdown
        v-if="isHistoryTable"
        id="historyTableActions"
        variant="link"
        toggle-class="text-decoration-none"
        no-caret
      >
        <template #button-content>
          <font-awesome-icon
            :icon="['fas', 'ellipsis-v']"
          />
        </template>
        <b-dropdown-item
          id="exportData"
          @click="exportData"
        >
          <font-awesome-icon
            :icon="['fas', 'cloud-download-alt']"
            class="mr-2"
          />
          Export
        </b-dropdown-item>
        <b-dropdown-item
          id="goToTableData"
          @click="goToTableView"
        >
          <font-awesome-icon
            :icon="['fas', 'table']"
            style="width: 1.25em;"
            class="mr-2"
          />
          Table Data
        </b-dropdown-item>
      </b-dropdown>
      <div
        v-if="isTodo && isAdmin()"
        class="d-flex align-items-center justify-content-end text-nowrap"
      >
        <label class="mb-0 mr-2 ">View As: </label>
        <div class="d-flex justify-content-space-between button-wrapper">
          <div class="d-flex badge-wrapper bg-light">
            <div
              v-if="selectedUser.name"
              class="d-flex pr-2 border-right"
            >
              <b-badge
                variant="info"
                size="sm"
                class="filter-item-badge"
              >
                <span class="badge-text">
                  {{ selectedUser.name }}
                </span>
                <span
                  v-b-tooltip.hover
                  title="Clear"
                  class="ml-1 delete-filter-item"
                  @click="clearSelectedUser"
                >
                  <font-awesome-icon :icon="['fas', 'times']" />
                </span>
              </b-badge>
            </div>
            <div :class="{'pl-2': selectedUser.name}">
              <b-dropdown
                id="viewAsUserDropdown"
                size="sm"
                variant="outline-primary"
                text="Choose User"
              >
                <b-dropdown-item
                  v-for="data in todoUserList"
                  :key="`ToDo-List-User-${data.userName}`"
                  :active="selectedUser.userGuid === data.userGuid"
                  @click="onUserSelected(data)"
                >
                  {{ data.displayName }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>
      </div>
    </div>
    <content-spinner :show="loading && isAudit" />
    <no-content
      :show="!getTotalRows && !loading"
      :text="noContentText"
    />
    <div
      v-if="!loading"
      :id="`${modalName}-table-container`"
      class="mb-3 table-responsive"
      :style="tableHeight"
      :class="{'show-dropdown': isShown}"
    >
      <b-table
        v-if="getTotalRows && !loading"
        :id="`${modalName}-table`"
        striped
        :small="small"
        :hover="hover"
        :fields="localFields"
        :items="items"
        :head-variant="'dark'"
        :per-page="isAudit ? 0 : perPage"
        :current-page="isAudit ? 1 : currentPage"
        :no-local-sorting="true"
        :style="getTableWidth"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        @row-clicked="rowClicked"
        @sort-changed="sortChanged"
        @filter="onFilter"
      >
        <template #cell()="{item, index, field}">
          <div
            class="td-content-wrapper"
            :class="{'td-editing': isInEditing(index, field, item)}"
          >
            <div
              v-if="editingCell.index === index"
              class="input-editor-wrapper"
              :class="'editor-wrapper-'+field.dataTypeName"
            >
              <lookup-input-component
                v-show="isMultiSelect(field) && isInEditing(index, field, item)"
                transit-ref="multi-select"
                :props-value="vModelData[index][field.name]"
                :is-validation-rules="false"
                :index="index"
                :item="item"
                :field="field"
                :is-upload-data="true"
                @on-select="onChangeSelect"
                @on-add-new-relation="onAddNewRelationalEntity"
              />
              <div v-if="isFieldText(field) && !isPostedStatus(field)">
                <b-form-textarea
                  v-if="isLineBreakTextField(field)"
                  :ref="`currentInput-${field.name}-${index}`"
                  v-model="vModelData[index][field.name]"
                  class="input-editor"
                  :disabled="field.disabled"
                  @blur="onChangeSelect(index, field, item)"
                />
                <b-form-input
                  v-else
                  :id="`${field.name}-${index + 1}-import-input`"
                  :ref="`currentInput-${field.name}-${index}`"
                  v-model="vModelData[index][field.name]"
                  type="text"
                  class="input-editor"
                  @blur="onChangeSelect(index, field, item)"
                  @keydown.enter="onChangeSelect(index, field, item)"
                />
              </div>
              <multiselect
                v-if="isPostedStatus(field)"
                :id="`select-${field.name}-${index + 1}-import-input`"
                :ref="`select-${field.name}-${index}`"
                v-model="vModelData[index][field.name]"
                :options="field.data.options"
                :track-by="field.config.optionValue"
                :label="field.config.optionLabel"
                :multiple="field.config.multiple"
                :close-on-select="field.config.closeOnSelect"
                :show-labels="false"
                @blur="onChangeSelect(index, field, item)"
                @close="onChangeSelect(index, field, item)"
              />
              <b-form-input
                v-if="(isFieldDecimal(field) || isFieldWholeNumber(field))"
                :id="`${field.name}-${index + 1}-import-input`"
                :ref="`currentInput-${field.name}-${index}`"
                v-model="vModelData[index][field.name]"
                type="number"
                class="input-editor"
                @blur="onChangeSelect(index, field, item)"
                @keydown.enter="onChangeSelect(index, field, item)"
                @keypress="filterDecimalField($event)"
              />
              <custom-date-picker
                v-if="isFieldDateTime(field)"
                :id="`${field.name}-${index + 1}-import-input`"
                :ref="`currentInput_${index}_${field.ordinalPosition}`"
                v-model="vModelData[index][field.name]"
                type="datetime"
                input-class="input-editor form-control"
                :disabled="field.disabled"
                class="editor-w"
                :format="getDateFormat(field)"
                :placeholder="getDateFormat(field)"
                :value-type="getDateFormat(field)"
                :open="customDateChange[`open_${index}_${field.ordinalPosition}`]"
                @input="onDatePickerInputChange"
                @close="onChangeSelect(index, field, item, $event)"
                @open="onDatePickerOpen($event, index, item, field)"
              />

              <custom-date-picker
                v-if="isFieldDate(field)"
                :id="`${field.name}-${index + 1}-import-input`"
                :ref="`currentInput_${index}_${field.ordinalPosition}`"
                v-model="vModelData[index][field.name]"
                input-class="input-editor form-control"
                class="editor-w"
                :disabled="field.disabled"
                :format="getDateFormat(field)"
                :value-type="getDateFormat(field)"
                :placeholder="getDateFormat(field)"
                :open="customDateChange[`open_${index}_${field.ordinalPosition}`]"
                @pick="onChangeSelect(index, field, item, $event)"
                @close="onChangeSelect(index, field, item, $event)"
                @open="onDatePickerOpen($event, index, item, field)"
              />
            </div>

            <div
              class="td-content"
              :column="field.name || field.key"
              :class="{'no-value': isEmptyValue(field, item)}"
              :title="isFieldBoolean(field) ? getItemTitle(field, item) : null"
              data-toggle="tooltip"
              data-placement="bottom"
              :style="getWrap"
              @mousedown="startCellEditing($event, index, item, field)"
              @blur="clearSelectedData"
            >
              <div
                v-if="(isFieldText(field) || isUserField(field)) && !isPostedStatus(field)"
                :id="`${field.name}-${index + 1}-import-text`"
                class="text-break td-actual-content"
                :class="getFieldClass(field, item[field.key])"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getItemTitle(field, item)"
              >
                {{ getLabel(field, item[field.key]) | capitalize }}
              </div>
              <div
                v-else-if="isPostedStatus(field)"
                :id="`${field.name}-${index + 1}-import-text`"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getItemTitle(field, item)"
              >
                {{ getSelectText(field, item) }}
              </div>
              <div
                v-else-if="isFieldNumber(field) && (isImported || isHistory)"
                :id="`${field.name}-${index + 1}-import-text`"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getItemTitle(field, item)"
              >
                {{ getNumberValue(item[field.name], field) }}
              </div>
              <span v-else-if="isFieldBoolean(field) && (isImported || isHistory)">
                <b-form-checkbox
                  :id="`${field.name}-${index + 1}-import`"
                  :checked="isChecked(item[field.name])"
                  :disabled="isHistory"
                  @change="onChangeSelect(index, field, item, $event)"
                />
              </span>
              <div
                v-else-if="isMultiSelect(field) && (isHistory || isImported)"
                :id="`${field.name}-${index + 1}-import-text`"
                :class="{'not-exist': checkSelectedValue(item, field, index) && isImported}"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getItemTitle(field, item)"
              >
                {{ getMultiSelectValue(item, field) }}
              </div>
              <span
                v-else-if="isFieldHierarchy(field) && isImported"
                :id="`${field.name}[${index + 1}]`"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getHierarchyText(item, field)"
              >
                {{ getHierarchyText(item, field) }}
              </span>
              <span
                v-else-if="isFieldHierarchy(field) && isHistory"
                :id="`${field.name}[${index + 1}]`"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getHistoryHierarchyText(field, item)"
              >
                {{ getHistoryHierarchyText(field, item) }}
              </span>
              <div
                v-else-if="isFieldDateTime(field) && isImported"
                :id="`${field.name}-${index + 1}-import-text`"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getItemTitle(field, item)"
              >
                {{ item[field.name] }}
              </div>
              <div
                v-else-if="isFieldDate(field) && isImported"
                :id="`${field.name}-${index + 1}-import-text`"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getItemTitle(field, item)"
              >
                {{ item[field.name] }}
              </div>
              <span
                v-else-if="isFieldBoolean(field)"
                :id="`${field.name}-${index + 1}`"
                :class="getFieldClass(field, item[field.key])"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="item[field.key] | capitalize"
              >
                {{ `${item[field.key]}` | capitalize }}
              </span>
              <span
                v-else-if="isFieldDateTime(field)"
                :id="`${field.name}-${index + 1}`"
                :class="getFieldClass(field, item[field.key])"
                :data-column="field.name"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="item[field.key] | toDatetime(dateTimeFormat(field))"
              >
                {{ item[field.key] | toDatetime(dateTimeFormat(field)) }}
              </span>
              <span
                v-else-if="isFieldDate(field)"
                :id="`${field.name}[${index + 1}]`"
                :class="getFieldClass(field, item[field.key])"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="item[field.key] | toDate(JSON.parse(field.dataTypeJSON).FormatString)"
              >
                {{ item[field.key] | toDate(JSON.parse(field.dataTypeJSON).FormatString) }}
              </span>
              <span
                v-else
                :id="`${$options.filters.displayName(field.key)}-${index + 1}`"
                :class="getFieldClass(field, item[field.key])"
                :data-column="field.name"
                data-toggle="tooltip"
                data-placement="bottom"
                :title="getLabel(field, item[field.key])"
              >
                {{ getLabel(field, item[field.key]) }}
              </span>
            </div>
          </div>
        </template>
        <template
          v-if="isImported || isTodo || isFileManipulationController"
          #cell(actions)="{item, index, field}"
        >
          <div
            v-if="isImported || isTodo || isFileManipulationController"
            class="d-flex justify-content-center"
          >
            <font-awesome-icon
              v-if="isValidated(item)"
              :id="`${field.key}-${index + 1}-confirm`"
              class="text-success action-button mr-2"
              title="Confirm"
              :icon="['fas', 'check']"
              @click="onConfirmClick(item)"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'check']"
              class="mr-2"
              style="opacity: 0"
            />
            <font-awesome-icon
              v-if="isTodo"
              :id="`${field.key}-${index + 1}-link`"
              class="text-primary action-button mr-2"
              title="Link"
              :icon="['fas', 'link']"
              @click="onLinkClick(item)"
            />
            <font-awesome-icon
              v-if="actionButtons.fileList && batchHasFiles(item)"
              class="text-primary action-button mr-2"
              title="File List"
              :icon="['fas', 'copy']"
              @click="onFileListClick(item)"
            />
            <div
              v-else
              style="width: 1.4em;"
            />
            <font-awesome-icon
              v-if="actionButtons.edit !== false && !isImported"
              :id="`${field.key}-${index + 1}-edit`"
              class="text-primary action-button mr-2"
              title="Edit"
              :icon="['fas', 'edit']"
              @click="onEditClick(item)"
            />
            <font-awesome-icon
              v-if="isTodo"
              :id="`${field.key}-${index + 1}-history`"
              title="History"
              class="text-warning action-button"
              :icon="['fas', 'history']"
              @click="onHistoryClick(item, index, field)"
            />
            <font-awesome-icon
              v-if="isImported || actionButtons.delete"
              :id="`${field.key}-${index + 1}-delete`"
              class="text-danger action-button mr-2"
              title="Delete"
              :icon="['fas', 'trash']"
              @click="onDeleteClick(item.rowIndex || index)"
            />
          </div>
        </template>
        <template
          #cell(recordStatus)="{item, index, field}"
          data-toggle="tooltip"
          data-placement="bottom"
          :title="getStatusValue(item)"
        >
          <span>{{ getStatusValue(item) }}</span>
        </template>
        <template #head()="data">
          {{ data.field.displayName || data.label }}
          <filter-dropdown
            v-if="showFilterDropdown(data)"
            :data="data"
            :options="options"
            :selected="selected"
            :filters="filters"
            @context-change="emitContextChange"
            @filter="onFilter"
            @drop-down-shown="onDropDownShown"
            @drop-down-hidden="onDropDownHidden"
            @sort-change="sortChanged"
          />
          <span
            v-if="showResizeColumn(data)"
            class="header-resize-action"
            @mousedown="onColumnResize($event, data.field)"
          />
        </template>
      </b-table>
    </div>
    <div class="d-flex justify-content-between align-items-center">
      <pagination-summary
        v-if="getTotalRows > 0"
        :total="getTotalRows"
        :page="currentPage"
        :per-page="perPage"
      />
      <b-pagination
        v-if="getTotalRows > perPage"
        v-model="currentPage"
        class="mb-0"
        :total-rows="getTotalRows"
        :per-page="perPage"
        @input="onCurrentPageChange"
      />
    </div>
    <edit-row-modal
      v-for="(value, modalName, index) in relationalModals"
      :modal-key="modalName"
      @changed-row="onChangedRow"
    />
  </b-card>
</template>

<script>
import NoContent from '../../NoContent';
import PaginationSummary from './PaginationSummary';
import FilterDropdown from './FilterDropdown';
import FilterHeader from './FilterHeader';
import {createNamespacedHelpers} from 'vuex';
import pixelWidth from 'string-pixel-width';
import _, {capitalize} from 'lodash';
import getFormattedNumber from '../../../filters/number.filter';
import ContentSpinner from '../../ContentSpinner';
import moment from 'moment';
import EditRowModal from '@/core/components/complex-table/EditRowModal.vue';
import LookupInputComponent from '@/core/components/LookupInputComponent.vue';
import {axiosClient} from '@/core/services/http.service';

const {mapState, mapGetters, mapActions} = createNamespacedHelpers('dataManagement');
const {mapState: userMapState} = createNamespacedHelpers('user');

export default {
  name: 'ComplexTableView',
  components: {
    LookupInputComponent,
    ContentSpinner,
    PaginationSummary,
    NoContent,
    FilterDropdown,
    FilterHeader,
    EditRowModal,
  },
  props: {
    actionButtons: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      required: true,
    },
    small: false,
    hover: Boolean,
    fields: Array,
    allFields: Array,
    size: {
      type: String,
      default: '',
    },
    totalRows: {
      type: Number,
      required: true,
    },
    batchUploadFieldsLength: Number,
    options: Array,
    selected: String,
    isImported: {
      type: Boolean,
      default: false,
    },
    isTodo: {
      type: Boolean,
      default: false,
    },
    isFileManipulationController: {
      type: Boolean,
      default: false,
    },
    isAudit: {
      type: Boolean,
      default: false,
    },
    isBatch: {
      type: Boolean,
      default: false,
    },
    todoUserList: Array,
    isHistoryView: {
      type: Boolean,
      default: false,
    },
    isHistoryModal: {
      type: Boolean,
      default: false,
    },
    fieldRowColors: {
      type: Array,
      default: () => [],
    },
    modalName: {
      type: String,
      required: true,
    },
    isImportReview: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortBy: null,
      sortDesc: false,
      currentPage: 1,
      perPage: 100,
      perPageOptions: [5, 10, 20, 50, 100],
      selectedOption: this.selected,
      isCreate: true,
      isShown: false,
      filters: {},
      editingCell: {
        rowIndex: null,
        columnKey: null,
        index: null,
        item: null,
        field: null,
      },
      customDateChange: {
        inputDate: '',
        inputEdit: false,
      },
      filterType: {
        field: null,
        value: null,
      },
      filterText: {
        field: null,
        value: null,
      },
      fromDate: null,
      toDate: null,
      columnWidths: [],
      vModelData: [],
      selectedUser: {
        name: '',
        userGuid: '',
      },
      isResizable: false,
      recordStatusField: {
        key: 'recordStatus',
        label: 'Record Status',
        tdClass: 'px-2 align-middle',
        thStyle: {
          width: '130px',
          position: 'sticky',
          zIndex: '10',
          top: '0',
        },
        thClass: 'exclude',
      },
      saveSearchMapForDebounce: new Map(),
      backupFields: [],
    };
  },
  computed: {
    ...mapState({
      relationalModals: state => state.table.relationalModals,
      schema: state => state.table.schema,
      tableData: state => state.tableData.data,
      isLookupFieldValuesLoading: state => state.pagedDataLookupValuesLoading,
      pagedDataLookups: state => state.pagedDataLookups,
      multiselectRefs: state => state.multiselectRefs,
    }),
    ...userMapState({
      currentUserPermission: state => state.currentUserAdpPermission.data.role,
    }),
    ...mapState([
      'validation',
      'tableData',
      'fieldValues',
      'permissionTypes',
    ]),
    localFields() {
      const stickyHeader = {
        position: 'sticky',
        zIndex: '10',
        top: '0',
      };
      const table = document.getElementById(`${this.modalName}-table-container`);
      const tableWidth = table ? table.offsetWidth : null;
      const fieldsSize = this.fields.length;
      const newFields = this.fields.map(f => {
        const width = this.isHistory ? this.columnWidths.find(cw => cw.key === f.key && cw.tableId === f.entityGuid) :
          this.columnWidths.find(cw => cw.key === f.key);
        let textWidth = this.isHistory || this.isImported ?
          pixelWidth(f.displayName, {size: 16}) : pixelWidth(f.label, {size: 16});
        textWidth = Math.round(textWidth);
        if (width) {
          f = {...f, thStyle: {width: `${width.width}px`}};
        } else if (textWidth <= 120) {
          if (fieldsSize < 5) {
            f = {...f, thStyle: {width: `${(tableWidth - 270) / fieldsSize}px`}};
          } else {
            f = {...f, thStyle: {width: '150px'}};
          }
        } else if (textWidth > 120 && fieldsSize > 1) {
          f = {...f, thStyle: {width: `${textWidth + 30}px`}};
        }
        if (f.key === 'actionsRequired') {
          f = {...f, thStyle: {width: '350px'}};
        }

        f.thStyle = this.isBatch ?
          {...f.thStyle, position: 'relative', paddingRight: '1.75rem'} :
          {...f.thStyle, ...stickyHeader};

        return f;
      });
      if (this.isImported || this.isTodo || this.isFileManipulationController) {
        return [
          ...newFields,
          {
            key: 'actions',
            tdClass: 'px-2 align-middle',
            thStyle: {...stickyHeader, width: '120px'},
          }];
      }
      if (this.isHistory) {
        const index = newFields.findIndex(f => f.isInternal);
        newFields.splice(index, 0, this.recordStatusField);
        return newFields;
      }
      return [...newFields];
    },
    getTotalRows() {
      if (this.totalRows < ((this.currentPage - 1) * this.perPage + 1)) {
        this.currentPage = 1;
      }
      return this.totalRows;
    },
    tableHeight() {
      return {
        height: this.isImported ? '500px' : this.isHistoryModal ? '600px' : '100%',
      };
    },
    getWrap() {
      return this.isTodo ? {'white-space': 'normal'} : {'white-space': 'nowrap'};
    },
    getTableWidth() {
      let width = 0;
      let columnsWithWidth = 0;
      const table = document.getElementById(`${this.modalName}-table-container`);
      this.localFields.forEach(f => {
        const localWidth = this.isHistory ? this.columnWidths.find(cw => cw.key === f.key && cw.tableId === f.entityGuid) :
          this.columnWidths.find(cw => cw.key === f.key);
        if (f.thStyle && f.thStyle.width) {
          width += parseInt(f.thStyle.width, 10);
          if (localWidth) {
            columnsWithWidth++;
          }
        }
      });
      return !columnsWithWidth && this.isAudit ? {width: '100%'} :
        width && (table && width > table.offsetWidth) ? {width: `${width}px`} :
          columnsWithWidth > 0 ? {width: 'auto'} :
            {width: '100%'};
    },
    isHistory() {
      return this.isHistoryView || this.isHistoryModal;
    },
    getTableViewStyle() {
      return this.isBatch ? {} : this.isImported ? {overflow: 'hidden'} : {height: '100%', overflow: 'hidden'};
    },
    isHistoryTable() {
      return this.$route.name === 'dataManagementHistoryTableView';
    },
    isImportPreview() {
      return this.isImported && !this.isImportReview;
    },
    noContentText() {
      return this.isImportReview ? 'No Invalid Rows Found' : 'No Data Available';
    },
  },
  methods: {
    ...mapActions(['addNewRecord', 'getPagedDataLookupValueItems']),
    formatDecimal(number, decimalPlaces) {
      const numString = number.toString();

      const [integerPart, decimalPart] = numString.split('.');

      const formattedDecimalPart = decimalPart
        ? decimalPart.padEnd(decimalPlaces, '0')
        : '0'.repeat(decimalPlaces);

      return `${integerPart || '0'}.${formattedDecimalPart}`;
    },
    getDateFormat(field) {
      const jsonData = JSON.parse(field.dataTypeJSON);
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';

      return `${format}${this.isFieldDate(field) ? '' : ' HH:mm:ss'}`;
    },
    onDatePickerInputChange() {
      this.vModelData = [...this.vModelData];
    },
    getOptions(field, item) {
      const suggestFieldName = item[`${field.name}__$LookupStatus`];
      return this.isValidSuggestions(suggestFieldName) ? suggestFieldName.options : field.data.options;
    },
    isValidSuggestions(suggestionName) {
      if (suggestionName) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        return suggestions && suggestions.length > 0;
      }
      return false;
    },
    onDatePickerOpen(event, index, item, field) {

      setTimeout(() => {
        this.addDatePickerKeydownListener(index, item, field);
      }, 50);

      event.target.addEventListener('click', () => {
        this.addDatePickerKeydownListener(index, item, field);
      });
    },
    addDatePickerKeydownListener(index, item, field) {
      const calendarPanel = document.getElementsByClassName('mx-datepicker-main mx-datepicker-popup');
      if (calendarPanel[0]) {
        const datePicker = calendarPanel[0];
        datePicker.tabIndex = 0;
        datePicker.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            this.onChangeSelect(index, field, item, e);
          }
          if (e.key === 'Escape') {
            this.onChangeSelect(index, field, item, e);
          }
        });
      }
    },
    getFormattedDate(field, value) {
      const dateFormat = this.getDateFormat(field);
      return this.isFieldDate(field) ?
        this.$options.filters.toDate(moment(value, dateFormat).toDate(), dateFormat) :
        this.$options.filters.toDatetime(moment(value, dateFormat).toDate(), dateFormat);
    },
    batchHasFiles(batch) {
      return batch.hasOwnProperty('batchFiles');
    },
    filterDecimalField(e) {
      const char = String.fromCharCode(e.keyCode);
      if (/^[0-9.,]+$/.test(char)) return true;
      else e.preventDefault();
    },
    isPostedStatus(field) {
      return this.schema.postedStatusGuid && field.fieldGuid ? this.schema.postedStatusGuid === field.fieldGuid : false;
    },
    isValidated(item) {
      return item['validationStatus'] === 'validated';
    },
    onConfirmClick(item) {
      this.$emit('on-confirm', item, this.selectedUser);
    },
    getSelectedValue(field, item) {
      this.selectedString = item[field.name];
    },
    getFieldClass(field, value) {
      const fieldData = this.fieldRowColors.find((el) => el.columnKey === field.key);
      if (fieldData) {
        const fieldOption = fieldData.options.find((el) => el.value.toLowerCase() === value.toLowerCase());
        return fieldOption ? fieldOption.class : '';
      }

      return '';
    },
    getLabel(field, value) {
      if (field.capitalize) {
        return this.$options.filters.capitalize(value);
      }
      return value;
    },
    getNumberValue(value, field) {
      const valueExist = value || value === 0;
      if (!valueExist) return '';
      if (isNaN(value)) return value;

      const json = JSON.parse(field.dataTypeJSON);
      if (this.isFieldDecimal(field)) {
        value = parseFloat(value).toFixed(json.DecimalsToDisplay);
      }

      return getFormattedNumber(value, json.FormatString, json.DecimalsToDisplay, this.isFieldDecimal(field), json.IsPercentage);
    },
    isInEditing(index, field, item) {
      if (this.isImported && !this.isImportReview) {
        return this.editingCell.rowIndex === index
            && this.editingCell.columnKey === field.name
            && (
              this.isMultiSelect(this.editingCell.field)
                || (this.isFieldDecimal(this.editingCell.field) || this.isFieldWholeNumber(this.editingCell.field))
                || (this.isFieldDate(this.editingCell.field) || this.isFieldDateTime(this.editingCell.field))
                || this.isFieldText(field)
            );
      }
    },
    clearSelectedData() {
      this.editingCell = {
        rowIndex: null,
        columnKey: null,
        index: null,
        item: null,
        field: null,
      };
    },
    onChangeSelect(index, field, item, value = null) {
      if(value) {
        item[field.name] = value;
      }
      const oldItem = JSON.parse(JSON.stringify(item));
      const bussinessKeyFieldExists = this.fields.some(field => field.isBusinessKey);

      const addError = (field, item, message, variant = 'danger') => {
        item._cellVariants = {...item._cellVariants, [field.name]: variant};
        if (variant === 'danger') item.errors = {...item.errors, [field.name]: message};
        if (variant === 'warning') item.warnings = {...item.warnings, [field.name]: message};
      };

      const addValidationRuleError = (item, message) => {
        item._rowVariant = 'danger';
        item.validationErrorMessage = message;
      };

      const isValidItem = async (field, item) => {
        const validationRulesJson = JSON.parse(this.tableData.data.validationRulesJson);


        const updatedItem = JSON.parse(JSON.stringify(item));
        this.fields.forEach(field => {
          if ([this.fieldValues.dateTime, this.fieldValues.date].includes(field.dataTypeName)) {
            const dateFormat = JSON.parse(field.dataTypeJSON).FormatString;
            updatedItem[field.name] = updatedItem[field.name] ? moment(updatedItem[field.name], `${dateFormat} HH:mm:ss`).format('YYYY/MM/DDTHH:mm:ss') : null;
          }
        });

        if (
          validationRulesJson.ValidationRules && validationRulesJson.ValidationRules.length
            || this.isPostedStatus(field)
            || bussinessKeyFieldExists
        ) {
          const params = {
            data: [updatedItem],
            tableId: this.$route.params.tableId,
            validateOnly: true,
            modalName: 'import',
          };

          const {success, body} = await this.addNewRecord(params);

          let isValid = true;
          if (success) {
            body.resultSetTemp.forEach(result => {
              if (result.uploadStatus === 'rejected' && result.validationErrors) {
                const validationRuleErrorIndex = result.validationErrors.findIndex(error => error.indexOf('Failed validation rule') > -1);
                const postStatusValidationErrorIndex = result.validationErrors.findIndex(error => error.indexOf('The same user can not post the record.') > -1);
                const businnessKeyValidationErrorIndex = result.validationErrors.findIndex(error => error.indexOf('Duplicate business key') > -1);

                if (validationRuleErrorIndex > -1) {
                  addValidationRuleError(item, result.validationErrors[validationRuleErrorIndex]);
                  isValid = false;
                } else if (businnessKeyValidationErrorIndex > -1) {
                  addValidationRuleError(item, result.validationErrors[businnessKeyValidationErrorIndex]);
                  isValid = false;
                }

                if (postStatusValidationErrorIndex > -1) {
                  addError(field, item, result.validationErrors[postStatusValidationErrorIndex]);
                  isValid = false;
                }
              }
            });
          }
          if (!isValid) {
            return false;
          }
        }

        if ((this.isFieldDate(field) || this.isFieldDateTime(field)) && item[field.name] === 'Invalid date') {
          addError(field, item, `"${field.name}" is required field`);
          return false;
        }

        if (field.isRequired && [null, ''].includes(item[field.name])) {
          addError(field, item, `"${field.name}" is required field`);
          return false;
        }

        if (this.isPostedStatus(field)) {
          if (!['Pending', 'Posted'].includes(item[field.name])) {
            addError(field, item, 'Value should be \'Pending\' or \'Posted\'');
            return false;
          }
          /*          else if (item[field.name] === 'Posted') {
            addError(field, item, `Unable to update records that are Posted`);
            return false;
          }*/
        }

        if (this.isFieldDecimal(field)) {
          if (item[field.name]) {
            const value = item[field.name].toString();
            const jsonData = JSON.parse(field.dataTypeJSON);

            const decimalArray = value.split('.');
            const scale = jsonData.Scale;
            const numberLength = value.replace(/[,.]/g, '').length;
            const precision = jsonData.Precision;

            if (decimalArray.length > 1 && decimalArray[decimalArray.length - 1].length > scale) {
              addError(
                field, item,
                `this value was formatted to ${decimalArray[decimalArray.length - 1].length} decimal places, which exceeds the allowed number of ${scale} decimal places, as per the table set up. This has been automatically trimmed to ${scale} decimal places`,
                jsonData.ApplyRounding ? 'warning' : 'danger',
              );
              item[field.name] = parseFloat(value.replace(/,/g, ''));
              if (jsonData.ApplyRounding) {
                item[field.name] = item[field.name].toFixed(scale);
              }
              return false;
            } else if (numberLength > precision) {
              addError(field, item, 'Number length more than precision');
              return false;
            }
          }
        }

        if (this.isFieldWholeNumber(field) && item[field.name] && Number.isFloat(item[field.name])) {
          addError(field, item, 'Value must be whole number, decimal entered');
          return false;
        }

        return true;
      };

      const removeError = (field, item) => {
        item._cellVariants = {...item._cellVariants, [field.name]: 'success'};
        if (item.options) delete item.options[field.name];
        if (item.errors) delete item.errors[field.name];
      };

      const removeWarning = (field, item) => {
        if (item.warnings) delete item.warnings[field.name];
        if (item._cellVariants) delete item._cellVariants[field.name];
      };

      const removeValidationRuleError = (item) => {
        delete item._rowVariant;
        delete item.validationErrorMessage;
      };

      const removeErrorsFromItem = async (field, item) => {
        if (!await isValidItem(field, item)) return false;

        if (this.isInvalidItem(item, field)) {
          removeError(field, item);
        }

        if (this.itemHasWarning(field, item)) {
          removeWarning(field, item);
        }

        removeValidationRuleError(item);
      };

      setTimeout(async () => {
        if (value === null) {
          if (this.editingCell.index === null || this.editingCell.index === undefined) {
            return;
          }
        } else {
          this.vModelData[index] = {...this.vModelData[index], [field.name]: value};
        }

        const newValue = this.vModelData[index][field.name];

        if (this.isMultiSelect(field)) {
          const backupOptions = this.backupFields.find(bf => bf.fieldGuid === field.fieldGuid).data.options;
          if (!backupOptions.find(bo => bo.id === newValue.id)) backupOptions.push(newValue);

          if (item.options[field.name] && item.options[field.name].oldValue) {
            const oldValue = item.options[field.name].oldValue;
            if (newValue.id === oldValue.id) {
              this.vModelData[index][field.name] = oldValue;
              if (item._cellVariants[field.name]) delete item._cellVariants[field.name];
            } else {
              if (item.options) delete item.options[field.name];
              item._cellVariants = {...item._cellVariants, [field.name]: 'success'};
            }
          }
          item[field.name] = newValue.id;
        } else if (this.isPostedStatus(field)) {
          if (newValue.value) {
            item[field.name] = newValue.value;
          } else {
            item[field.name] = oldItem[field.name];
          }
        } else {
          if (this.isFieldDate(field) || this.isFieldDateTime(field)) {
            item[field.name] = this.getFormattedDate(field, newValue);
          } else {
            item[field.name] = newValue;
          }
        }

        // Detect if the current error is part of business key error and take all the fields that are part of business key error
        const businessKeyErroredFields = [];
        for (const key in item.errors) {
          if (item.errors[key].includes('Duplicate business key')) {
            businessKeyErroredFields.push(key);
          }
        }

        await removeErrorsFromItem(field, item);

        // Find another items with business key errors on the above fields
        if (businessKeyErroredFields.length) {
          const otherErroredItems = this.scanTheTableForRemovingDuplicateBusinessKeyErrors(businessKeyErroredFields);

          // If there is exactly one item errored because of duplicate business key remove the error from there as well.
          // If there are more than one the error should stay.
          if (otherErroredItems.length === 1) {
            await removeErrorsFromItem(field, otherErroredItems[0]);
          }
        }

        this.clearSelectedData();
        this.$emit('on-change-select', item, item.rowIndex);
      });
    },
    scanTheTableForRemovingDuplicateBusinessKeyErrors(businessKeyErroredFields) {
      return this.items.filter(item => {
        if (!item.validationErrorMessage || !item.validationErrorMessage.includes('Duplicate business key')) {
          return false;
        }
        for (const f of businessKeyErroredFields) {
          if (item.errors && item.errors[f]) {
            return true;
          }
        }
        return false;
      });
    },
    onAddNewRelationalEntity(item, index, field) {
      this.$emit('on-add-new-relational-entity', item, index, field);
    },
    async startCellEditing(e, index, item, field) {
      if (this.isCalculatedLookup(field)) {
        return;
      }
      if (this.isImportReview) {
        return;
      }

      if (![
        this.fieldValues.lookup,
        this.fieldValues.decimalNumber,
        this.fieldValues.floatNumber,
        this.fieldValues.wholeNumber,
        this.fieldValues.text,
        this.fieldValues.date,
        this.fieldValues.dateTime,
      ].includes(field.dataTypeName) || !this.isImported) {
        return;
      }

      if (!this.vModelData[index]) {
        this.vModelData[index] = {};
      }

      if (this.isMultiSelect(field) && item.options[field.name]) {
        const foundItem = field.data
          ? field.data.options.find((elem) => elem.text === item[field.name])
          : undefined;

        if (foundItem === undefined) {
          this.vModelData[index][field.name] = item.options[field.name].oldValue;
        } else {
          this.vModelData[index][field.name] = {id: foundItem.text, text: foundItem.text};
        }
      } else {
        this.vModelData[index][field.name] = item[field.name];
      }

      this.editingCell = {
        rowIndex: index,
        columnKey: field.name,
        index,
        item,
        field,
      };
      // if (field.dataTypeName === this.fieldValues.lookup && this.isInEditing(index, field, item)) {
      //   const { body } = await this.getPagedDataLookupValueItems({ field, query: '' });
      //   const mdmDataTypeJSON = JSON.parse(body.mdmDataTypeJSON);
      //   const dataTypeName = mdmDataTypeJSON.UnderlyingDataType.split('.')[1];
      //
      //   const existingOptions = JSON.parse(JSON.stringify(field.data.options));
      //
      //   let firstOptions;
      //
      //   if (dataTypeName === 'Decimal') {
      //     const scale = mdmDataTypeJSON.Scale;
      //     firstOptions = [...body.data.map(val => ({
      //       id: val.recordGuid,
      //       text: this.formatDecimal(val.value, scale).split(',').join(''),
      //     }))];
      //   } else {
      //     firstOptions = [...body.data.map(item => ({ id: item.recordGuid, text: item.value }))];
      //   }
      //
      //   if (existingOptions.length < 50) {
      //     const combinedOptions = _.uniqBy(existingOptions.concat(firstOptions), 'id');
      //     // .slice(0, 50);
      //     let sortedOptions;
      //
      //     if (dataTypeName === 'Decimal') {
      //       sortedOptions = combinedOptions.sort((a, b) => a.text - b.text);
      //     } else {
      //       sortedOptions = combinedOptions.sort((a, b) => {
      //         return !!a.text ? a.text.localeCompare(b.text) : 0;
      //       });
      //     }
      //
      //     field.data.options = JSON.parse(JSON.stringify(sortedOptions));
      //   } else {
      //     field.data.options = JSON.parse(JSON.stringify(existingOptions));
      //   }
      // }
    },
    showOldValue(field, item) {
      if (this.isInvalidItem(item, field)) {
        let oldValue;
        if (this.isMultiSelect(field)) {
          oldValue = item.options[field.name].oldValue.text;
        }
        return `Old value was ${oldValue}`;
      }
      if (item.errors && item.errors.length > 0) {
        return item.errors.map(error => error).join('. ');
      }
    },
    checkSelectedValue(item, field) {
      if (this.isInvalidItem(item, field)) {
        const content = item.options[field.name];

        if (content && content.customClass) {
          if (this.isMultiSelect(field)) {
            return item[field.name] === content.oldValue.id;
          } else {
            return item[field.name] === content.oldValue;
          }
        }
      }
    },
    isUserField(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'user';
    },
    isFieldNumber(field) {
      const type = field.fieldType || field.dataTypeName;
      return ['wholeNumber', 'decimalNumber', 'floatNumber'].includes(type);
    },
    isFieldDecimal(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === this.fieldValues.decimalNumber;
    },
    isFieldWholeNumber(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === this.fieldValues.wholeNumber;
    },
    isFieldText(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'text';
    },
    isLineBreakTextField(field) {
      const dataTypeJSON = JSON.parse(field.dataTypeJSON);
      return dataTypeJSON.IsLineBreak;
    },
    isFieldBoolean(field) {
      return field.dataTypeName === 'boolean';
    },
    isFieldDateTime(field) {
      const type = field.fieldType || field.dataTypeName;
      return [this.fieldValues.dateTime, this.fieldValues.lastUpdatedDate, this.fieldValues.createdDate].includes(type);
    },
    dateTimeFormat(field) {
      return field.dataTypeJSON && JSON.parse(field.dataTypeJSON).FormatString ? `${JSON.parse(field.dataTypeJSON).FormatString} HH:mm:ss` : '';
    },
    isFieldDate(field) {
      const type = field.fieldType || field.dataTypeName;
      return ['date'].includes(type);
    },
    isFieldCheckbox(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'boolean';
    },
    isFieldRecordStatus(field) {
      const type = field.fieldType || field.key;
      return type === this.fieldValues.recordStatus;
    },
    isMultiSelect(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'lookup';
    },
    isFieldHierarchy(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'parentChildHierarchy';
    },
    isFieldInternal(field) {
      const type = field.fieldType || field.dataTypeName;
      return [this.fieldValues.lastUpdatedBy].includes(type);
    },
    isFieldBusinessKey(field) {
      return field.isBusinessKey;
    },
    isInvalidItem(item, field, isButton = false) {
      if (isButton) {
        return item.options && Object.keys(item.options).length > 0 || item._rowVariant || item._cellVariants;
      }
      return item.options && item.options[field.name] && item.options[field.name].customClass || item.errors && item.errors[field.name];
    },
    itemHasWarning(field, item) {
      return item.warnings && item.warnings[field.name];
    },
    showFilterDropdown(data) {
      return data.field.key !== 'actions';
    },
    async onDeleteClick(index) {
      this.$emit('on-delete', index);
    },
    onFileListClick(item) {
      this.$emit('file-list-click', item);
    },
    onLinkClick(item) {
      this.$emit('on-link', item);
    },
    onEditClick(item) {
      this.$emit('on-edit', item);
    },
    getMultiSelectValue(item, field) {
      const elem = field.data.options.find((elem) => item !== null && elem.id === item[field.name]);
      if (this.isHistory) {
        this.selectedString = elem === undefined ? '[Deleted Value]' : elem.text;
        return this.selectedString;
      } else if (this.isImported) {
        const currentPagedDataLookups = this.pagedDataLookups.find(obj => obj.id === item[field.name]);
        return [null, '<EMPTY>'].includes(item[field.name]) ? '<EMPTY>' : elem ? elem.text : currentPagedDataLookups?.text;
      }
    },
    getHierarchyText(item, field, isExport = false) {
      const hierarchy = field.data.options.hierarchyField;
      const options = field.data && field.data.options ? field.data.options.items : [];
      const hierarchyFieldName = isExport ? hierarchy.primary.name : hierarchy.display.name;
      const chosenOptions = options.find((elem) => item !== null && elem[hierarchyFieldName] === item[field.name]);
      if (chosenOptions) {
        if (isExport) {
          return chosenOptions[field.data.options.hierarchyField.display.name];
        } else {
          return options.find((elem) => item !== null && elem[hierarchy.primary.name] === chosenOptions[hierarchy.primary.name])[hierarchy.display.name];
        }
      }
      return item ? item[field.name] : '';
    },
    getHistoryHierarchyText(field, item) {
      const options = field.data && field.data.options ? field.data.options.items : [];
      const chosenOptions = options.find((elem) => item !== null && elem[field.data.options.hierarchyField.primary.name] === item[field.name]);
      if (chosenOptions) {
        return chosenOptions[field.data.options.hierarchyField.display.name];
      }
      return item ? item[field.name] : '';
    },
    getStatusValue(item) {
      return item[this.validation.status] === this.validation.validationFailed ? 'Validation Failed' : capitalize(item[this.validation.status]);
    },
    isChecked(item) {
      return ['true', '1', true, 1].includes(typeof item === 'string' ? item.toLowerCase() : item);
    },
    rowClicked(e) {
      this.$emit('row-clicked', e);
    },
    focusOnInput($event, key) {
      this.$refs[`filterInput-${key}`].focus();
    },
    sortChanged(event) {
      this.$emit('sort-changed', event);
    },
    addParamsToLocation(params) {
      history.pushState(
        {},
        null,
        '/#' + this.$route.path +
          '?' +
          Object.keys(params).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key])).join('&'),
      );
    },
    onFilter(data) {
      this.currentPage = 1;
      this.resetScrollData();
      this.filters = data.updatedFilter;
      this.$emit('filter', data.query);
      if (!['auditLogView', 'dataManagementHistoryTableView'].includes(this.$route.name)) {
        return;
      }
      const queryParams = {};
      Object.keys(data.query.filter).map((key) => {
        queryParams[key] = {
          fieldType: data.query.filter[key].fieldType,
          label: data.query.filter[key].label,
        };
        if (this.isUserField(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            selectItems: data.query.filter[key].selectItems,
          };
        }
        if (this.isFieldNumber(data.query.filter[key]) || this.isFieldText(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            text: data.query.filter[key].text,
            type: data.query.filter[key].type,
          };
        }
        if (this.isMultiSelect(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            selectItems: data.query.filter[key].selectItems,
          };
        }
        if (this.isFieldHierarchy(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            field: data.query.filter[key].field,
            selectItems: data.query.filter[key].selectItems,
          };
        }
        if (this.isFieldDateTime(data.query.filter[key]) || this.isFieldDate(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            fromDate: data.query.filter[key].fromDate,
            toDate: data.query.filter[key].toDate,
          };
        }
        if (this.isFieldCheckbox(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            selected: data.query.filter[key].selected,
          };
        }
        if (this.isFieldRecordStatus(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            selectedStatus: data.query.filter[key].selectedStatus,
          };
        }
        if (this.isFieldInternal(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            value: data.query.filter[key].text,
          };
        }
      });
      this.addParamsToLocation({...this.$route.query, filters: JSON.stringify(queryParams)});
    },
    resetScrollData() {
      const table = document.getElementById(`${this.modalName}-table-container`);
      table.scrollTop = 0;
      table.scrollLeft = 0;
    },
    onDropDownShown() {
      this.isShown = true;
    },
    onDropDownHidden() {
      this.isShown = false;
    },
    buildQuery(filters) {
      return {id: this.$route.params.tableId, filter: filters, limit: 20, offset: 0, page: 1};
    },
    onPerPageChange() {
      this.contextChanged();
      this.$emit('per-page-change', {perPage: this.perPage, currentPage: this.currentPage});
    },
    onCurrentPageChange() {
      this.contextChanged();
      this.$emit('current-page-change', {perPage: this.perPage, currentPage: this.currentPage});
    },
    onUserSelected(data) {
      this.selectedUser = {
        name: data.displayName,
        userGuid: data.userGuid,
      };
      this.$emit('on-user-selected', data);
    },
    clearSelectedUser() {
      this.selectedUser = {};
      this.$emit('clear-selected-user');
    },
    contextChanged() {
      this.resetScrollData();
      const context = {
        pagination: {
          perPage: this.perPage,
          page: this.currentPage,
        },
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        filters: this.filters,
      };
      this.emitContextChange(context);
    },
    emitContextChange(context) {
      this.$emit('context-change', context);
    },
    getLocalStorageItemName() {
      return `${this.modalName.replace('-', '_').toUpperCase()}_TABLE_SIZES`;
    },
    onColumnResize(event, field) {
      const table = document.getElementById(`${this.modalName}-table`);
      if (table) {
        const th = event.target.parentElement;
        const tableHead = table.children[0];
        let tableId = '';
        let moving = false;

        if (field.entityGuid) {
          tableId = field.entityGuid;
        }

        if (th.nodeName === 'TH') {
          const isDynamicTable = this.isBatch || this.isTodo;
          let textWidth = isDynamicTable ? pixelWidth(field.label, {size: 16}) : pixelWidth(field.displayName, {size: 16});
          textWidth = Math.round(textWidth);

          th.style.minWidth = `${textWidth + 40}px`;
          tableHead.style.pointerEvents = 'none';
          tableHead.style.userSelect = 'none';
          table.style.cursor = 'col-resize';

          moving = true;

          const cutPx = str => +str.replace('px', '');

          table.addEventListener('mousemove', (e) => {
            if (moving) {
              if (this.isTodo) {
                th.style.width = `${cutPx(th.style.width) + e.movementX}px`;
              } else {
                th.style.width = `${th.offsetWidth + e.movementX}px`;
              }
              table.style.width = `${table.offsetWidth + e.movementX}px`;
              if (cutPx(th.style.width) < cutPx(th.style.minWidth)) {
                moving = false;
                th.style.width = th.style.minWidth;
              }
              moving = true;
            }
          });

          document.addEventListener('mouseup', () => {
            if (!moving) return;
            moving = false;

            tableHead.style.pointerEvents = '';
            table.style.cursor = 'default';

            this.getWidths();

            const thItems = tableHead.children[0].children;
            const excludedItems = [];

            for (const idx in thItems) {
              if (thItems[idx].className !== 'exclude') {
                excludedItems.push(thItems[idx]);
              }
            }

            for (const idx in this.fields) {
              const key = this.fields[idx].key;
              const index = this.columnWidths.filter(cw => cw.tableId ? cw.tableId === tableId : true)
                .map(cw => cw.key)
                .indexOf(key);

              if (index === -1) {
                this.columnWidths.push({
                  key: key,
                  width: excludedItems[idx].clientWidth,
                  tableId: field.entityGuid,
                });
              } else {
                this.columnWidths[index].width = excludedItems[idx].clientWidth;
              }
              localStorage.setItem(this.getLocalStorageItemName(), JSON.stringify(this.columnWidths)); //MONITORING_RESIZE
            }
          });
        }
      }
    },
    isStaticColumn(field) {
      return field.key === this.fieldValues.recordStatus || field.key === 'actions' || field.key === 'actionsRequired';
    },
    getWidths() {
      const resize = localStorage.getItem(this.getLocalStorageItemName());
      if (resize) {
        this.columnWidths = JSON.parse(resize);
      }
    },
    getTableName(field) {
      return `Add New ${this.tableData.data.displayName}`;
    },
    onChangedRow(result) { 
      if (result.insertData.resultSetTemp.length > 0) {
        this.fields.map(async (field) => {
          if (field.relationalTableId === result.insertData.entityGuid) {
            const {body} = await axiosClient.get(`api/field/${JSON.parse(field.dataTypeJSON).FieldToDisplayGuid}`);      
            const newOption = {
              id: result.updatedId,
              text: result.insertData.updatedRecord[body.name],
            };
            field.data.options.push(newOption);
            this.$nextTick(()=>{
              const refs = this.multiselectRefs[`multi-select-${result.field.name}-${result.rowIndex + 1}`];
              refs && refs.select(newOption);             
              this.isImportPreview && this.$emit('on-import-lookup-add', field, newOption);
            });        
          }
          return field;
        });
      }
      this.$emit('update-todo-list-data', this.selectedUser);
    },
    getFiltersFromUrl() {
      if (this.$route.query.filters) {
        this.filters = JSON.parse(decodeURIComponent(this.$route.query.filters));
      }
    },
    getSelectText(field, item) {
      const options = field.data && field.data.options ? field.data.options : [];
      const chosenOptions = options.find((elem) => elem.value === item[field.name]) || {};

      if (Object.keys(chosenOptions).length) {
        return chosenOptions.value ? chosenOptions.text : '';
      }

      return item[field.name];
    },
    isAdmin() {
      return this.currentUserPermission === 'admin';
    },
    goToTableView() {
      const params = this.$route.params;
      this.$router.push({
        name: 'dataManagementTableView',
        params: {tableId: params.tableId},
        query: {moduleUuid: params.moduleUuid},
      });
    },
    canEdit() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ].includes(this.currentUserPermission);
    },
    exportData() {
      const fields = JSON.parse(JSON.stringify(this.allFields));

      const index = fields.findIndex(f => f.isInternal);
      fields.splice(index, 0, this.recordStatusField);

      const header = fields.map((elem) => elem.displayName || elem.label); // columns name
      const customFields = {
        requiredFields: {},
        dateFields: {},
        dateTimeFields: {},
        wholeNumberFields: {
          separation: {},
          nonSeparation: {},
        },
        decimalFields: {
          separation: {},
          nonSeparation: {},
        },
      };
      fields.forEach(f => {
        const json = f.dataTypeJSON ? JSON.parse(f.dataTypeJSON) : '';

        if (f.isRequired && !f.isInternal) {
          customFields.requiredFields[f.name] = f;
        }
        if (this.isFieldDate(f)) {
          customFields.dateFields[f.name] = f;
        }
        if (this.isFieldDateTime(f)) {
          customFields.dateTimeFields[f.name] = f;
        }
        if (['comma', 'dot'].includes(json.FormatString)) {
          if (this.isFieldWholeNumber(f)) {
            customFields.wholeNumberFields.separation[f.name] = f;
          }
          if (this.isFieldDecimal(f)) {
            customFields.decimalFields.separation[f.name] = f;
          }
        } else {
          if (this.isFieldWholeNumber(f)) {
            customFields.wholeNumberFields.nonSeparation[f.name] = f;
          }
          if (this.isFieldDecimal(f)) {
            customFields.decimalFields.nonSeparation[f.name] = f;
          }
        }
      });

      const exportData = this.items.map((item) => {
        const result = {};
        fields.forEach((f) => {
          let val = item[f.name || f.key];

          if (this.isFieldRecordStatus(f)) {
            val = this.getStatusValue(item);
          }
          /*else if (this.isFieldNumber(f)) {
            val = this.getNumberValue(val, f, true)
          } else if (this.isFieldDateTime(f)) {
            val = this.$options.filters.toDatetime(val, 'YYYY/MM/DD HH:mm:ss');
          } else if (this.isFieldDate(f)) {
            val = this.$options.filters.toDate(val, 'YYYY/MM/DD');
          }*/
          else if (this.isFieldDate(f)) {
            // Date.parse(val) -> date to timestamp
            // 8.64e7 -> 1 day
            const days = Math.round((Date.parse(val) - new Date(1899, 11, 30)) / 8.64e7);
            const date = parseInt((days).toFixed(10));

            val = isNaN(date) ? null : date;
          } else if (this.isFieldDateTime(f)) {
            // date to timestamp / 1day + 1970/01/01 + 4:00AM
            //const date = Date.parse(val) / 8.64e7 + 25569 + 0.166666667;
            const date = moment.utc(val).valueOf() / 8.64e7 + 25569; // to align with export dateTime on tableView
            val = isNaN(date) ? null : date;
          } else if (this.isFieldCheckbox(f)) {
            val = val ? 'True' : 'False';
          } else if (this.isPostedStatus(f)) {
            val = this.getSelectText(f, item);
          } else if (this.isFieldHierarchy(f)) {
            val = this.getHierarchyText(item, f, true);
          } else if (this.isMultiSelect(f)) {
            val = this.getMultiSelectValue(item, f, true);
          }

          result[f.name || f.label] = val;
        });
        return result;
      });

      const result = {};
      fields.forEach((f) => {
        result[f.name || f.label] = null;
      });
      if (exportData.length === 0) {
        exportData.push(result);
      }

      const params = {
        header,
        exportData,
        customFields,
      };

      this.$emit('on-export', params);
    },
    showResizeColumn(data) {
      return !this.isStaticColumn(data.field) && (!this.isImported || this.isImportPreview);
    },
    isEmptyValue(field, item) {
      return [null, ''].includes(item[field.name]) && !this.isFieldBoolean(field);
    },
    getError(field, item) {
      if (item.validationErrorMessage) return item.validationErrorMessage;
      return item.errors[field.name];
    },
    getWarning(field, item) {
      return item.warnings[field.name];
    },
    cellHasError(field, item) {
      return item.errors && item.errors[field.name] || item.validationErrorMessage;
    },
    cellHasWarning(field, item) {
      return item.warnings && item.warnings[field.name];
    },
    getItemValue(field, item) {
      let value;
      if (this.isFieldText(field)) {
        value = capitalize(this.getLabel(field, item[field.key]));
      } else if (this.isFieldNumber(field)) {
        value = this.getNumberValue(item[field.name], field);
      } else if (this.isMultiSelect(field)) {
        const lookupItem = field.data.options.find(o => o.id === item[field.name]);
        if (lookupItem) value = lookupItem.text;
      } else {
        value = item[field.name];
      }

      return value;
    },
    getItemTitle(field, item) {
      if (this.cellHasError(field, item)) return this.getError(field, item);
      if (this.cellHasWarning(field, item)) return this.getWarning(field, item);

      return this.getItemValue(field, item);
    },
    onHistoryClick(item, index, field) {
      this.$emit('on-history-click', item, index, field);
    },
    isCalculatedLookup(field) {
      if (field.dataTypeName === this.fieldValues.lookup) {
        let dataTypeJSON;

        if (field.dataTypeJSON) dataTypeJSON = JSON.parse(field.dataTypeJSON);

        return dataTypeJSON.IsCalculated;
      }
    },
  },
  async mounted() {
    this.getWidths();
    this.getFiltersFromUrl();
    this.backupFields = JSON.parse(JSON.stringify(this.fields));
  },
};
</script>

<style lang="scss">
@import "../../../scss/variables";

.action-button {
  cursor: pointer;
}

.complex-table-view {
  white-space: nowrap;

  .show-dropdown {
    overflow: auto;
  }

  .per-page-select {
    width: 100px;
  }

  .button-wrapper {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }

  > .table-responsive {
    > table {
      border-bottom: 1px solid #ddd;
      table-layout: fixed;
      margin-bottom: 0;
      // width: auto;

      > thead {
        > tr {
          > th {
            background: #0F355E;

            .header-resize-action {
              position: absolute;
              cursor: col-resize;
              right: 0;
              top: 0;
              bottom: 0;
              border-left: 5px solid transparent;
              height: 30px;
            }

            .header-actions {
              position: absolute;
              right: 0;
              top: 0;
              bottom: 0;
              width: $complex-table-header-dropdown-caret-width;
              height: 30px;

              .dropdown-toggle {
                padding: 0;
                border-radius: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                border-left: $table-border-width solid $table-border-color;
                border-right: $table-border-width solid $table-border-color;

                &:hover {
                  background-color: #326194;
                }
              }

              .dropdown-menu {
                min-width: 14rem;
              }

              .dropdown-item,
              .b-dropdown-form {
                outline: none !important;
              }

              .dropdown-header {
                padding-left: 1rem;
                padding-right: 1rem;
              }

              .dropdown-item {
                svg {
                  width: 1em;
                }
              }

              .dropdown-header {
                font-size: 0.75rem;
              }
            }

            &:hover {
              background-color: #326194;
            }
          }
        }
      }

      > tbody {
        > tr {
          > td {
            max-width: 150px;
            //width: 150px;
            padding: 0;
          }
        }
      }

      .td-content-wrapper {
        width: 100%;
        height: 100%;

        .input-editor-wrapper {
          display: none;
          position: relative;

          .input-editor {
            display: block;
            width: 100%;
            margin: 0;
            padding: 4px 7px;
            border-radius: 0;
            line-height: 22px;
            height: auto;
            box-shadow: none;
            border: 1px solid $primary
          }

          .button-toolbar {
            position: absolute;
            top: 100%;
            left: 50%;
            width: 160px;
            margin-top: -2px;
            margin-left: -80px;
            background-color: #FFF;
            padding: 4px 8px;
            border: 1px solid $primary;
            border-top: 2px solid #FFF;
            display: flex;
            justify-content: center;
          }

          &.editor-wrapper-select {
            .input-editor {
              padding: 0;
            }

            button {
              padding: 4px 7px;
              line-height: 20px;
              border-radius: 0;
              width: 100%;
              text-align: left;
            }

            .dropdown-menu {
              width: 100%;
              min-width: 0;
              border-radius: 0;

              > li {
                > a {
                  padding: 4px 7px;
                }
              }
            }
          }
        }

        .td-content {
          padding: 4px 8px;
          line-height: 24px;
          width: 100%;
          min-height: 32px;
          height: 100%;
          overflow: hidden;
          text-overflow: ellipsis;

          .not-exist {
            text-decoration: red wavy underline;
          }
        }

        &.td-editing {
          .input-editor-wrapper {
            display: block;
          }

          .td-content {
            display: none;
          }

        }
      }

      &.table.b-table.table-sm > thead > tr > [aria-sort]:not(.b-table-sort-icon-left),
      &.table.b-table.table-sm > tfoot > tr > [aria-sort]:not(.b-table-sort-icon-left) {
        background-position: right calc(#{$table-cell-padding} / 2 + #{$complex-table-header-dropdown-caret-width}) center;
        padding-right: calc(#{$table-cell-padding} + #{$b-table-sort-icon-bg-width} + #{$complex-table-header-dropdown-caret-width});
      }
    }
  }

  .multiselect__single {
    font-size: 13px;
    color: black;
    padding: 0;
  }

  .multiselect__option,
  .multiselect__option span {
    font-size: 13px;
    color: black;
  }

  .multiselect__input,
  .multiselect__input::placeholder {
    font-size: 13px;
  }

  .multiselect,
  .multiselect__tags,
  .multiselect__select,
  .multiselect__option,
  .multiselect__option::after {
    min-height: 32px;
    height: 32px;
  }

  .multiselect__select {
    height: 100%;
  }

  .multiselect__option {
    padding: 8px;
  }

  .multiselect__tags {
    padding: 5px 40px 0 8px;
  }

  .multiselect__option--disabled {
    //opacity: 0.5;
  }

  .lkp__multiselect--wrapper {
    margin-bottom: 0;
  }

}

.button-wrapper {
  form {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;

    fieldset {
      margin: 0;
    }
  }

  button {
    z-index: 2;
  }

  .form-group {
    .invalid-feedback {
      display: none !important;
    }

    .input-label {
      display: none;
    }
  }

  .form-wrapper {
    input {
      transform: translateX(100%);
      opacity: 0;
      transition: opacity .25s, transform .25s;
      z-index: -1;

      &:focus {
        transform: translateX(0);
        opacity: 1;
        z-index: 1;
      }
    }

    button {
      white-space: nowrap;
    }

    &:hover {
      input {
        transform: translateX(0);
        opacity: 1;
        z-index: 1;
      }
    }
  }

  .badge-wrapper {
    width: auto;
    border-radius: 0.5rem;
    border: 1px solid $gray-300;
    padding: 0.3rem;

    .filter-item-badge {
      padding: 0;
      line-height: unset !important;

      .badge-text {
        display: inline-block;
        padding: 0.3rem 0 0.3rem 0.75rem;
      }

      .delete-filter-item {
        display: inline-block;
        padding: 0.3rem;
        border-top-right-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;
        cursor: pointer;

        &:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
      }
    }
  }

  .button-wrapper-inner {
    justify-content: center;
    align-items: center;
  }
}

.main-header-wrapper {
  align-items: baseline;
}

.no-value {
  padding: 0 !important;

  div {
    min-height: 31px;
  }
}
</style>
